import { DEFAULT_AUTH_STATUS } from '@/store/auth/auth.constants';
import {
  clearAuthSession,
  getStoredAuthSession,
  saveAuthSession,
} from '@/store/auth/authStorage';
import { useReconnectSessionOnNetworkBack } from '@/store/auth/hooks/useReconnectSessionOnNetworkBack';
import {
  checkNetworkConnectivity,
  isLikelyNetworkError,
} from '@/utils/network/network.utils';
import * as AuthSession from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { whisApiAxiosInstance } from '@/api/WhisApi/whisApi.instance';
import { useIsWeb } from '@/hooks/useIsWeb';
import { AzureAdConfiguration } from '@api/WhisApi/configuration/configuration.types';
import {
  AuthContextValue,
  AuthStatus,
  AuthTokens,
  AuthUserFetchStatus,
  AuthUserState,
  OpenIdScopes,
} from './auth.types';
import { buildTokenPayload, getRedirectUri } from './auth.utils';

WebBrowser.maybeCompleteAuthSession();

export const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined,
);

const refreshBufferMs = 60 * 1000;

type AuthProviderProps = {
  children: React.ReactNode;
  azureAdConfiguration: AzureAdConfiguration;
};

export function AuthProvider({
  children,
  azureAdConfiguration,
}: AuthProviderProps): React.ReactNode {
  const [status, setStatus] = useState<AuthStatus>(DEFAULT_AUTH_STATUS);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isReconnectingSession, setIsReconnectingSession] = useState(false);
  const [hasRestoredSession, setHasRestoredSession] = useState(false);
  const [user, setUser] = useState<AuthUserState['user']>(null);
  const [userFetchStatus, setUserFetchStatus] =
    useState<AuthUserFetchStatus>('idle');
  const [userFetchError, setUserFetchError] =
    useState<AuthUserState['error']>(null);

  const interceptorIdRef = useRef<number | null>(null);
  const refreshPromiseRef = useRef<Promise<AuthTokens | null> | null>(null);

  const isWeb = useIsWeb();
  const redirectUri = useMemo(() => getRedirectUri(isWeb), [isWeb]);
  const clientId = azureAdConfiguration.clientId ?? '';
  const necessaryScopes = useMemo(
    () => [
      OpenIdScopes.openId,
      OpenIdScopes.profile,
      OpenIdScopes.offline_access,
      azureAdConfiguration.backendScope,
    ],
    [azureAdConfiguration.backendScope],
  );

  const microsoftDiscovery = useMemo<AuthSession.DiscoveryDocument>(
    () => ({
      authorizationEndpoint: `https://login.microsoftonline.com/${azureAdConfiguration.issuer}/oauth2/v2.0/authorize`,
      tokenEndpoint: `https://login.microsoftonline.com/${azureAdConfiguration.issuer}/oauth2/v2.0/token`,
      revocationEndpoint: `https://login.microsoftonline.com/${azureAdConfiguration.issuer}/oauth2/v2.0/logout`,
    }),
    [azureAdConfiguration.issuer],
  );

  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: clientId,
      scopes: necessaryScopes,
      redirectUri,
      responseType: AuthSession.ResponseType.Code,
      prompt: AuthSession.Prompt.SelectAccount,
      usePKCE: true,
    },
    microsoftDiscovery,
  );

  const updateUserFetchState = useCallback(
    (nextState: Partial<AuthUserState>) => {
      if ('user' in nextState) {
        setUser(nextState.user ?? null);
      }

      if ('status' in nextState) {
        setUserFetchStatus(nextState.status ?? 'idle');
      }

      if ('error' in nextState) {
        setUserFetchError(nextState.error ?? null);
      }
    },
    [],
  );

  /**
   * Persist ONLY the refresh token in secure store, other tokens in memory.
   */
  const persistTokens = useCallback(
    async (nextTokens: AuthTokens | null) => {
      setTokens(nextTokens);

      if (nextTokens?.accessToken) {
        const authorizationHeader = `Bearer ${nextTokens.accessToken}`;
        whisApiAxiosInstance.defaults.headers.common.Authorization =
          authorizationHeader;
      } else {
        delete whisApiAxiosInstance.defaults.headers.common.Authorization;
      }

      if (nextTokens?.refreshToken) {
        await saveAuthSession(nextTokens);
        setStatus('authenticated');
        return;
      }

      await clearAuthSession();
      setStatus('unauthenticated');
      updateUserFetchState({ status: 'idle', user: null, error: null });
    },
    [updateUserFetchState],
  );

  /**
   * Refresh tokens using the persisted refresh token.
   */
  const refreshTokens = useCallback(
    async (refreshToken: string): Promise<AuthTokens | null> => {
      try {
        const refreshed = await AuthSession.refreshAsync(
          {
            clientId: clientId,
            refreshToken,
            scopes: necessaryScopes,
          },
          microsoftDiscovery,
        );

        const payload = buildTokenPayload(refreshed, refreshToken);

        await persistTokens(payload);
        return payload;
      } catch (error) {
        if (isLikelyNetworkError(error)) {
          const isOnline = await checkNetworkConnectivity();
          if (!isOnline) {
            // Keep persisted refresh token for degraded offline mode handling outside auth.
            setStatus('unauthenticated');
            updateUserFetchState({ status: 'idle', user: null, error: null });
            return null;
          }
        }

        console.error('[AUTH] refresh failed', error);
        await persistTokens(null);
        return null;
      }
    },
    [
      persistTokens,
      clientId,
      updateUserFetchState,
      microsoftDiscovery,
      necessaryScopes,
    ],
  );

  const ensureFreshTokens =
    useCallback(async (): Promise<AuthTokens | null> => {
      if (!tokens?.refreshToken || !tokens?.expiresAt) return tokens;

      const remainingMs = tokens.expiresAt - Date.now();
      if (remainingMs > refreshBufferMs) return tokens;

      if (refreshPromiseRef.current) {
        return await refreshPromiseRef.current;
      }

      refreshPromiseRef.current = refreshTokens(tokens.refreshToken);

      try {
        return await refreshPromiseRef.current;
      } finally {
        refreshPromiseRef.current = null;
      }
    }, [tokens, refreshTokens]);

  /**
   * Restore session on cold start.
   */
  const restoreSession = useCallback(async () => {
    if (hasRestoredSession) return;

    const stored = await getStoredAuthSession();

    if (!stored?.refreshToken) {
      setStatus('unauthenticated');
      updateUserFetchState({ status: 'idle', user: null, error: null });
      setHasRestoredSession(true);
      return;
    }

    const isOnline = await checkNetworkConnectivity();
    if (!isOnline) {
      setStatus('unauthenticated');
      updateUserFetchState({ status: 'idle', user: null, error: null });
      setHasRestoredSession(true);
      return;
    }

    await refreshTokens(stored.refreshToken);

    setHasRestoredSession(true);
  }, [hasRestoredSession, refreshTokens, updateUserFetchState]);

  /**
   * Handle authorization code exchange.
   */
  const handleLoginResponse = useCallback(
    async (authorizationCode: string) => {
      if (!request?.codeVerifier) {
        console.warn('[AUTH] missing PKCE codeVerifier');
        setIsLoggingIn(false);
        return;
      }

      try {
        const tokenResponse = await AuthSession.exchangeCodeAsync(
          {
            clientId: clientId,
            code: authorizationCode,
            redirectUri,
            extraParams: {
              code_verifier: request.codeVerifier,
            },
            scopes: necessaryScopes,
          },
          microsoftDiscovery,
        );

        const payload = buildTokenPayload(tokenResponse);

        await persistTokens(payload);
      } catch (error) {
        console.error('[AUTH] exchangeCodeAsync failed', error);
      } finally {
        setIsLoggingIn(false);
      }
    },
    [persistTokens, redirectUri, request, clientId, microsoftDiscovery, necessaryScopes],
  );

  /**
   * Starts the login flow.
   */
  const login = useCallback(async () => {
    if (!promptAsync) return;
    if (!request?.codeVerifier) return;

    setIsLoggingIn(true);

    try {
      const result = await promptAsync();

      if (result.type === 'success' && result.params?.code) {
        await handleLoginResponse(result.params.code);
      } else {
        setIsLoggingIn(false);
      }
    } catch (error) {
      console.error('[AUTH] login error', error);
      setIsLoggingIn(false);
    }
  }, [promptAsync, request, handleLoginResponse]);

  /**
   * Logout and clear local session.
   */
  const logout = useCallback(async () => {
    setIsLoggingIn(false);
    await persistTokens(null);

    if (isWeb && microsoftDiscovery.revocationEndpoint) {
      const logoutUrl = `${
        microsoftDiscovery.revocationEndpoint
      }?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;

      await WebBrowser.openBrowserAsync(logoutUrl);
    }
  }, [persistTokens, redirectUri, isWeb, microsoftDiscovery.revocationEndpoint]);

  /**
   * Restore session on app start.
   */
  useEffect(() => {
    if (isLoggingIn) return;
    restoreSession();
  }, [restoreSession, isLoggingIn]);

  useReconnectSessionOnNetworkBack({
    hasRestoredSession,
    status,
    isLoggingIn,
    setStatus,
    setIsReconnectingSession,
    refreshTokens,
  });

  /**
   * Web / iOS fallback: response may arrive here instead of promptAsync.
   */
  useEffect(() => {
    if (response?.type === 'success' && response.params?.code) {
      void handleLoginResponse(response.params.code);
    }
  }, [response, handleLoginResponse]);

  /**
   * Set up axios interceptor to add Authorization header when tokens change.
   */
  useEffect(() => {
    // Remove previous interceptor if it exists
    if (interceptorIdRef.current !== null) {
      whisApiAxiosInstance.interceptors.request.eject(interceptorIdRef.current);
    }

    // Add new interceptor with current token
    interceptorIdRef.current = whisApiAxiosInstance.interceptors.request.use(
      async (requestConfig) => {
        const refreshedTokens = await ensureFreshTokens();
        const accessToken = refreshedTokens?.accessToken;

        if (accessToken) {
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }
        return requestConfig;
      },
      (error) => Promise.reject(error),
    );

    // Cleanup on unmount
    return (): void => {
      if (interceptorIdRef.current !== null) {
        whisApiAxiosInstance.interceptors.request.eject(
          interceptorIdRef.current,
        );
      }
    };
  }, [ensureFreshTokens, tokens?.accessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      tokens,
      isAuthenticated: status === 'authenticated',
      isLoggingIn,
      isReconnectingSession,
      login,
      logout,
      user,
      userFetchStatus,
      userFetchError,
      updateUserFetchState,
    }),
    [
      status,
      tokens,
      isLoggingIn,
      isReconnectingSession,
      login,
      logout,
      user,
      userFetchStatus,
      userFetchError,
      updateUserFetchState,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
